import { useSyncExternalStore } from 'react';

/**
 * How this browser installs the app, if it can (roadmap item app-installable):
 *
 * - `prompt`: a Chromium browser fired `beforeinstallprompt`; the page keeps
 *   it and offers its own button (`InstallOffer`), which opens the browser's.
 * - `ios`: Safari on an iPhone or an iPad has no prompt and no event; the
 *   app is installed from its share sheet, so the page says where (web card
 *   225, 2026-10-19: «en iPhone no m'apareix aquesta opció»).
 * - `none`: already installed (opened from the home screen), installed just
 *   now, or a browser with neither (Firefox on a desktop, an app's own web view).
 */
export type InstallWay = 'none' | 'prompt' | 'ios';

/** The event Chromium fires when the app can be installed; not in TypeScript's DOM types. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/** Opened from the home screen: the browser's own word, and Safari's older one. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const media = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  return media || (navigator as { standalone?: boolean }).standalone === true;
}

/**
 * An iPhone's or an iPad's browser that has the share sheet's «Afegeix a la
 * pantalla d'inici»: Safari, and Chrome and Firefox there (every iOS browser
 * is Safari's engine). An iPad says it is a Mac, with a touch screen. A web
 * view inside another app (Discord's) does not say Safari and has no such
 * entry, so it gets no hint.
 */
export function isIosBrowser(
  ua = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  touchPoints = typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints,
): boolean {
  const apple = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1);
  return apple && /Safari\//.test(ua);
}

export function installWay(): InstallWay {
  if (installed || isStandalone()) return 'none';
  if (deferred) return 'prompt';
  if (isIosBrowser()) return 'ios';
  return 'none';
}

/** Opens the browser's own install dialog; once asked, the button goes. */
export async function promptInstall(): Promise<void> {
  const event = deferred;
  if (!event) return;
  deferred = null;
  notify();
  try {
    await event.prompt();
  } catch {
    // The browser refused to show it twice: nothing to do.
  }
}

export const installPrompt = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  /** For tests: as if the browser had fired `beforeinstallprompt`, or nothing. */
  reset(event: BeforeInstallPromptEvent | null = null, done = false) {
    deferred = event;
    installed = done;
    notify();
  },
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // The page offers it where the navigation is, instead of the browser's bar.
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    deferred = null;
    notify();
  });
}

export function useInstallWay(): InstallWay {
  return useSyncExternalStore(installPrompt.subscribe, installWay, () => 'none');
}
